import { defineStore } from "pinia";
import { computed, reactive, Ref, ref, watch } from "vue";
import BusinessRequestFormAdress from "../components/business-request-form/steps/BusinessRequestFormAdress.vue";
import BusinessRequestFormImages from "../components/business-request-form/steps/BusinessRequestFormImages.vue";
import BusinessRequestFormAuth from "../components/business-request-form/steps/BusinessRequestFormAuth.vue";
import BusinessRequestFormAuthLogin
    from "@/pages/index/components/business-request-form/steps/BusinessRequestFormAuthLogin.vue";
import BusinessRequestFormKeyWords from "../components/business-request-form/steps/BusinessRequestFormKeyWords.vue";
import BusinessRequestFormPresentation
    from "../components/business-request-form/steps/BusinessRequestFormPresentation.vue";
import BusinessRequestFormTitle from "../components/business-request-form/steps/BusinessRequestFormTitle.vue";
import { businessRequestTitleSchema } from "@/pages/index/validation/businessRequestTitleSchema.ts";
import { businessRequestUrgencySchema } from "@/pages/index/validation/businessRequestUrgencySchema.ts";
import BusinessRequestFormUrgency from "../components/business-request-form/steps/BusinessRequestFormUrgency.vue";
import BusinessRequestFormDescription
    from "../components/business-request-form/steps/BusinessRequestFormDescription.vue";
import BusinessRequestFormConfirmation
    from "../components/business-request-form/steps/BusinessRequestFormConfirmation.vue";
import { createUserAuthSession, isAuth, isLocalhost } from "@/utils/functions";
import { createOrUpdateBusinessrequest, uploadBusinessrequestImages } from "@/services/modules/BusinessRequestService";
import { BusinessRequest } from "@/models/BusinessRequest";
import { BusinessRequestBuilder } from "@/models/builders/BusinessRequestBuilder";
import { IBusinessRequestImage, IUser } from "@/models/interfaces/interfaces";
import { businessRequestSignUpSchema } from "@/pages/index/validation/businessRequestSignUpSchema.ts";
import { getProfileInfo, signInAsClient, signUpAsClientOnBusinessRequest } from "@/services";
import { businessRequestLoginSchema } from "@/pages/index/validation/businessRequestLoginSchema.ts";
import constants from "@/utils/constants.ts";
import { User } from "@/models/User.ts";
import { useUserStore } from "@/stores/user.ts";
import { useAlertsStore, useFormAlertsStore } from "@/stores/alerts.ts";
import useAuthStore from "@/stores/auth.ts";
import { logNewEvent } from '@/services/modules/firebase';
import i18n from "@/localization/i18n";

const t = i18n.global.t


export const useBusinessRequestFormStore = defineStore('business-request-form', () => {
    const userStore = useUserStore()
    const alerts = useAlertsStore()
    const loginAlerts = useFormAlertsStore()
    const auth = useAuthStore()
    const isCaptchaRequired = !isLocalhost()

    type FormContext = 'presentation' |
        'title' |
        'urgency' |
        'address' |
        'description' |
        'images' |
        'keywords' |
        'signup' |
        'login' |
        'confirmation'

    // Navigation

    const is_modal_visible = ref(false)
    const current_context: Ref<FormContext> = ref('presentation')
    const lastTitleUsed = ref<string | null>(null)

    const posCode = ref<string | null>(null)
    const isFromSeller = ref<boolean>(false)
    const source = ref<string>('WEB-TOPELA')

    const steps = {
        presentation: {
            component: BusinessRequestFormPresentation,
            validator: null,
            index: 0
        },
        title: {
            component: BusinessRequestFormTitle,
            validator: businessRequestTitleSchema,
            index: 1
        },
        urgency: {
            component: BusinessRequestFormUrgency,
            validator: businessRequestUrgencySchema,
            index: 2
        },
        address: {
            component: BusinessRequestFormAdress,
            validator: null,
            index: 3
        },
        description: {
            component: BusinessRequestFormDescription,
            validator: null,
            index: 4
        },
        images: {
            component: BusinessRequestFormImages,
            validator: null,
            index: 5
        },
        keywords: {
            component: BusinessRequestFormKeyWords,
            validator: null,
            index: 6
        },
        signup: {
            component: BusinessRequestFormAuth,
            validator: null,
            index: 7,
        },
        login: {
            component: BusinessRequestFormAuthLogin,
            validator: null,
            index: 7
        },
        confirmation: {
            component: BusinessRequestFormConfirmation,
            validator: null,
            index: 8
        }
    }
    const current_step_component = computed(() => steps[current_context.value].component)
    const current_progress_percentage = computed(() => {
        const total = isAuth() ? 7 : 8
        if (current_context.value === 'keywords' || current_context.value === 'images') {
            return '80'
        }
        if (current_context.value === 'signup' || current_context.value === 'login') {
            return '90'
        }

        return steps[current_context.value].index > 0 ? `${Math.round((steps[current_context.value].index / total) * 100)}` : '0'
    })

    const next_step = (context: FormContext, validate = true) => {
        const validator = steps[current_context.value].validator
        if (validator && validate) {
            validator.validate(current_form_data.value, { abortEarly: false })
                .then(() => {
                    // TODO: Use a validated data object
                    current_context.value = context
                })
                .catch((err) => {
                    err.inner.forEach((e: any) => {
                        // @ts-ignore
                        form_errors_list.value[current_context.value][e.path] = e.message;
                    });
                })
        } else {
            current_context.value = context
        }
    }

    const prev_step = (context: FormContext) => {
        current_context.value = context
    }



    // Form data
    const titleFormData = reactive({
        title: ''
    })

    const urgencyFormData = reactive({
        urgency: ''
    })

    const addressFormData = reactive<{
        addressItem: any;
    }>({
        addressItem: null,
    });

    const descriptionFormData = reactive({
        description: ''
    })

    const imagesFormData = reactive<{
        imagesFiles: any[],
        images: any[],
        selectedOption: number | null,
        client_consent: boolean
    }>({
        imagesFiles: [],
        images: [],
        selectedOption: null,
        client_consent: false
    });

    const keyswordFormData = reactive<{
        selectedOption: number | null,
        keywordsArray: any[],
        tagsList: any[],
        keywords: string | null,
    }>({
        selectedOption: null,
        keywordsArray: [],
        tagsList: [],
        keywords: null
    });

    const signUpFormData = reactive({
        email: '',
        first_name: '',
        last_name: '',
        phone: '',
        terms: false,
        cloudflare_captcha_token: ''
    })

    const loginFormData = reactive({
        email: '',
        password: '',
        cloud_flare_token: ''
    })

    const forgotPasswordFormData = reactive({
        email: ''
    })

    // Form errors
    const titleFormErrors = reactive({
        title: ''
    })

    const urgencyFormErrors = reactive({
        urgency: ''
    })

    //hasChanges
    const hasChanges = ref(false)
    const showHasChangesWarning = ref(false)
    const fromModal = ref(false)
    const wasUserAuthenticatedBeforeStarting: Ref<number | null> = ref(null) //0 means the user was not auth, 1 he was

    const isFromArticles = ref(false)

    // @ts-ignore
    const businessRequest: BusinessRequest | null = ref(null)

    const businessRequestData = reactive<{
        businessRequest: BusinessRequest | null,
    }>({
        businessRequest: null,
    });


    const signUpFormErrors = reactive({
        email: '',
        first_name: '',
        last_name: '',
        phone: '',
        terms: '',
    })

    const loginFormErrors = reactive({
        email: '',
        password: '',
        cloud_flare_token: ''
    })

    // Form validation
    const form_data_list = computed(() => {
        return {
            signup: signUpFormData,
            title: titleFormData,
            urgency: urgencyFormData,
            address: addressFormData,
            description: descriptionFormData,
            login: loginFormData,
            images: null,
            keywords: null,
            confirmation: null,
            presentation: null
        }
    })

    const form_errors_list = computed(() => {
        return {
            signup: signUpFormErrors,
            title: titleFormErrors,
            urgency: urgencyFormErrors,
            login: loginFormErrors,
            description: null,
            address: null,
            images: null,
            keywords: null,
            confirmation: null,
            presentation: null
        }
    })

    const current_form_data = computed(() => {
        return form_data_list.value[current_context.value]
    })

    const constructPayload = (user: any, posCode: string | null = null, isFromSeller: boolean = false, source: string = 'WEB-TOPELA'): any => {
        let payload: any = {
            "client_id": user.party_id ?? null,
            "title": titleFormData.title,
            "urgency": urgencyFormData.urgency,
            "description": descriptionFormData.description,
            "keywords": Object.values(keyswordFormData.keywordsArray || []),
            "town": addressFormData.addressItem.city,
            "zipcode": addressFormData.addressItem.postcode,
            "long": addressFormData.addressItem.x,
            "lat": addressFormData.addressItem.y,
            "requesting_party_visible": false,
            "source": source
        }

        if (posCode) {
            payload.client_consent = imagesFormData.client_consent
            payload.pos_code = posCode
            payload.is_from_seller = isFromSeller
        }

        return payload
    }

    const constructSignUpPayload = (posCode: string | null = null, isFromSeller: boolean = false, source: string = 'WEB-TOPELA'): any => {
        let payload: any = {
            "title": titleFormData.title,
            "urgency": urgencyFormData.urgency,
            "description": descriptionFormData.description,
            "keywords": Object.values(keyswordFormData.keywordsArray || []),
            "town": addressFormData.addressItem.city,
            "zipcode": addressFormData.addressItem.postcode,
            "long": addressFormData.addressItem.x,
            "lat": addressFormData.addressItem.y,
            "requesting_party_visible": false,
            "source": source
        }

        if (posCode) {
            payload.client_consent = imagesFormData.client_consent
            payload.pos_code = posCode
            payload.is_from_seller = isFromSeller
        }

        return payload
    }

    const imagesPayload = () => {
        const filesArray: File[] = imagesFormData.imagesFiles.map(image => image.file);
        return filesArray
    }

    const submit = async (payload: any, filesArray: File[]): Promise<string | null> => {
        try {
            const response = await createOrUpdateBusinessrequest(payload, filesArray);
            const businessRequestResponse: BusinessRequest = new BusinessRequestBuilder()
                .setBusinessRequest(response)
                .build();

            businessRequestData.businessRequest = businessRequestResponse;
            hasChanges.value = false;
            return null
        } catch (createError) {
            // console.log("createError")
            // console.log(createError)
            return 'creating';
        }
    };


    const clearFormsData = () => {
        keyswordFormData.keywords = null
        keyswordFormData.keywordsArray = []
        keyswordFormData.tagsList = []
        keyswordFormData.selectedOption = null

        imagesFormData.images = []
        imagesFormData.imagesFiles = []
        imagesFormData.selectedOption = null
        imagesFormData.client_consent = false

        posCode.value = null
        isFromSeller.value = false
        source.value = 'WEB-TOPELA'

        urgencyFormData.urgency = ''
        urgencyFormErrors.urgency = ''

        addressFormData.addressItem = null

        descriptionFormData.description = ''

        current_context.value = 'presentation'

        signUpFormData.email = ''
        signUpFormData.first_name = ''
        signUpFormData.last_name = ''
        signUpFormData.phone = ''
        signUpFormData.terms = false

        signUpFormErrors.email = ""
        signUpFormErrors.first_name = ""
        signUpFormErrors.last_name = ""
        signUpFormErrors.phone = ""
        signUpFormErrors.terms = ""

        loginFormData.email = ''
        loginFormData.password = ''
        loginFormData.cloud_flare_token = ''
        loginFormErrors.email = ""
        loginFormErrors.password = ""
        loginFormErrors.cloud_flare_token = ""

        titleFormData.title = ''
        titleFormErrors.title = ""
        lastTitleUsed.value = null
    }

    // Component loaders
    const sign_up_loading = ref(false)
    const login_loading = ref(false)

    // Component specific functions
    const signUpUser = () => {
        // alerts.clearAlerts()
        // loginAlerts.clearAlerts()
        signUpFormErrors.email = ''
        signUpFormErrors.first_name = ''
        signUpFormErrors.last_name = ''
        signUpFormErrors.phone = ''
        signUpFormErrors.terms = ''

        sign_up_loading.value = true

        businessRequestSignUpSchema
            .validate(signUpFormData, { abortEarly: false })
            .then(() => {
                sign_up_loading.value = true
                let payload = {
                    "email": signUpFormData.email,
                    "cloud_flare_token": isCaptchaRequired ? signUpFormData.cloudflare_captcha_token : "dev",
                    "is_captcha_required": isCaptchaRequired,
                    "first_name": signUpFormData.first_name,
                    "last_name": signUpFormData.last_name,
                    "phone": signUpFormData.phone,
                    "business_request": constructSignUpPayload(posCode.value, isFromSeller.value, source.value)
                }

                // console.log(imagesPayload())

                signUpAsClientOnBusinessRequest(payload, imagesPayload())
                    .then(async (response) => {
                        sign_up_loading.value = false

                        let businessRequestResponse = response.data.business_request
                        if (businessRequestResponse === 'error') {
                            const dismissRequestPostModal = document.getElementById("dismissRequestPostModal")
                            dismissRequestPostModal?.click()

                            const businessRequestSaveErrorButton = document.getElementById("businessRequestSaveErrorButton")
                            businessRequestSaveErrorButton?.click()

                            sign_up_loading.value = false
                            return
                        }
                        delete response.data.business_request

                        // Create a temporary auth session
                        createUserAuthSession(response.data, false).then(async () => {
                            const user = response.data
                            userStore.addUser(user as IUser)
                            wasUserAuthenticatedBeforeStarting.value = 0

                            businessRequestData.businessRequest = businessRequestResponse;
                            hasChanges.value = false;

                            // Get full object of client
                            // some fields are necessary for the request you'll make
                            // careful with token passing
                            getProfileInfo()
                                .then(async (response) => {
                                    let user_info: User = response.data

                                    sessionStorage.setItem('user', JSON.stringify(user_info))
                                    userStore.addUser(user_info)
                                    auth.setIsLoggedIn(true)
                                    login_loading.value = false
                                    alerts.putAlert({ 'code': 'logInSuccess', 'scope': 'general' })

                                    logNewEvent("presta_request_submit_user_infos")
                                    next_step('confirmation')
                                })
                                .catch(async (err: any) => {
                                    login_loading.value = false
                                    auth.setIsLoggedIn(true)
                                    alerts.putAlert({ 'code': 'logInSuccess', 'scope': 'general' })

                                    logNewEvent("presta_request_submit_user_infos")
                                    next_step('confirmation')
                                })
                        })

                        logNewEvent("presta_request_submit_user_infos")
                        next_step('confirmation')
                    }).catch((err) => {
                        sign_up_loading.value = false

                        if (err.code === "ERR_NETWORK" || !err.response || !err.response.data) {
                            alerts.putAlert({ code: 'savaBusinessrequesFail', scope: 'general' })
                            return
                        }
                        if (err.response.data.code === 'Err-15') {
                            alerts.putAlert({ code: 'invalidCaptcha', scope: 'general' })
                            return
                        } else if (err.response.data.code === 'Err-17') {
                            signUpFormErrors.email = t('w_msg_email_already_in_use')
                        } else {
                            alerts.putAlert({ code: 'savaBusinessrequesFail', scope: 'general' })
                            return
                        }
                    })
            })
            .catch((error) => {
                sign_up_loading.value = false
                error.inner.forEach((e: any) => {
                    // @ts-ignore
                    signUpFormErrors[e.path] = e.message;
                });
            })
    }

    const loginUser = () => {
        alerts.clearAlerts()
        loginAlerts.clearAlerts()
        loginFormErrors.email = ''
        loginFormErrors.password = ''
        loginFormErrors.cloud_flare_token = ''

        businessRequestLoginSchema
            .validate(loginFormData, { abortEarly: false })
            .then(() => {
                login_loading.value = true
                signInAsClient({
                    "username": loginFormData.email,
                    "password": loginFormData.password,
                    "keep_connected": "true",
                    "cloud_flare_token": isCaptchaRequired ? loginFormData.cloud_flare_token : "dev",
                    "is_captcha_required": isCaptchaRequired
                }).then((response) => {
                    //this bloc verifies that the user trying to login is no blocked
                    if (response.status === 200 && response.data.is_active === false) {
                        alerts.putAlert({ 'code': constants.LOGIN_FAILED_MESSAGE, 'scope': 'login' })
                        return
                    }

                    createUserAuthSession(response.data, true).then(() => {
                        const user = response.data
                        userStore.addUser(user as IUser)
                        wasUserAuthenticatedBeforeStarting.value = 1

                        let payload = constructPayload(user, posCode.value, isFromSeller.value, source.value)

                        // Get full object of client
                        // some fields are necessary for the request you'll make
                        // careful with token passing
                        getProfileInfo()
                            .then(async (response) => {
                                let user_info: User = response.data

                                sessionStorage.setItem('user', JSON.stringify(user_info))
                                userStore.addUser(user_info)
                                auth.setIsLoggedIn(true)
                                login_loading.value = false
                                alerts.putAlert({ 'code': 'logInSuccess', 'scope': 'general' })

                                submit(payload, imagesPayload()).then((response) => {
                                    if (response !== null) {
                                        const dismissRequestPostModal = document.getElementById("dismissRequestPostModal")
                                        dismissRequestPostModal?.click()

                                        const businessRequestSaveErrorButton = document.getElementById("businessRequestSaveErrorButton")
                                        businessRequestSaveErrorButton?.click()

                                        login_loading.value = false
                                        return
                                    }
                                    //show the last step
                                    logNewEvent("presta_request_submit_user_infos")
                                    next_step('confirmation')
                                })
                            })
                            .catch(async (err: any) => {
                                login_loading.value = false
                                auth.setIsLoggedIn(true)
                                alerts.putAlert({ 'code': 'logInSuccess', 'scope': 'general' })

                                submit(payload, imagesPayload()).then((response) => {
                                    if (response !== null) {
                                        const dismissRequestPostModal = document.getElementById("dismissRequestPostModal")
                                        dismissRequestPostModal?.click()

                                        const businessRequestSaveErrorButton = document.getElementById("businessRequestSaveErrorButton")
                                        businessRequestSaveErrorButton?.click()

                                        login_loading.value = false
                                        return
                                    }
                                    //show the last step
                                    logNewEvent("presta_request_submit_user_infos")
                                    next_step('confirmation')
                                })
                            })
                    })
                }).catch(async (err) => {
                    login_loading.value = false
                    loginFormData.password = ''

                    if (err.code === "ERR_NETWORK" || !err.response || !err.response.data) {
                        alerts.putAlert({ code: 'savaBusinessrequesFail', scope: 'general' })
                    } else if (err.response.status === constants.STATUS_LOGIN_FAILLED) {
                        loginAlerts.putAlert({ 'code': constants.LOGIN_FAILED_MESSAGE, 'scope': 'login' })
                    } else if (err.response.status === constants.STATUS_EXCEEDED_LIMIT) {
                        loginAlerts.putAlert({ 'code': constants.LOGIN_ATTEMPT_LIMIT_MESSAGE, 'scope': 'login' })
                    } else if (err.response.data.code === 'Err-15') {
                        loginAlerts.putAlert({ 'code': 'invalidCaptcha', 'scope': 'login' })
                        loginFormData.cloud_flare_token = ''
                    } else {
                        alerts.putAlert({ code: 'savaBusinessrequesFail', scope: 'general' })
                    }
                })
            })
            .catch((error) => {
                error.inner.forEach((e: any) => {
                    // @ts-ignore
                    loginFormErrors[e.path] = e.message;
                });
            })
    }

    return {
        is_modal_visible,
        current_step_component,
        current_progress_percentage,
        titleFormData,
        titleFormErrors,
        urgencyFormData,
        addressFormData,
        descriptionFormData,
        imagesFormData,
        keyswordFormData,
        urgencyFormErrors,
        hasChanges,
        showHasChangesWarning,
        fromModal,
        wasUserAuthenticatedBeforeStarting,
        signUpFormData,
        signUpFormErrors,
        loginFormData,
        forgotPasswordFormData,
        loginFormErrors,
        sign_up_loading,
        login_loading,
        current_context,
        isFromArticles,
        posCode,
        isFromSeller,
        source,
        next_step,
        prev_step,
        constructPayload,
        imagesPayload,
        submit,
        clearFormsData,
        signUpUser,
        loginUser,
        lastTitleUsed
        }
})
